/**
 * ParticipantsModal Component
 * Shows a modal with all ritual members (joined) and saved users in a grid card layout
 */

import { MoreVertical, Search, X } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { ChatBubbleIcon, HeartFilledIcon, HeartOutlineIcon, WarningIcon } from "@/components/icons"
import { Avatar } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useCreateDmConversation } from "@/features/chats/hooks/useDmChat"
import { useTranslation } from "@/language"
import { getRitualMembers, getRitualSavedUsers } from "../../services/ritual.service"
import type { RitualMember, RitualSavedUser } from "../../types/detail.types"

interface ParticipantsModalProps {
  ritualId: string
  currentUserId?: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

type Tab = "joined" | "saved"

interface ParticipantsMeta {
  joinedCount: number
  savedCount: number
}

interface PageData<T> {
  data: T[]
  total: number
  hasMore: boolean
  page: number
  meta: ParticipantsMeta
}

export function ParticipantsModal({
  ritualId,
  currentUserId,
  open,
  onOpenChange,
}: ParticipantsModalProps) {
  const { t } = useTranslation("rituals")
  const [searchQuery, setSearchQuery] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [activeTab, setActiveTab] = useState<Tab>("joined")
  const [page, setPage] = useState(1)
  const [isLoadingMembers, setIsLoadingMembers] = useState(false)
  const [isLoadingSaved, setIsLoadingSaved] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const activeTabRef = useRef(activeTab)
  const prevActiveTabRef = useRef<Tab>(activeTab)

  // Keep refs in sync with state
  useEffect(() => {
    activeTabRef.current = activeTab
    // Detect tab change and reset page
    if (prevActiveTabRef.current !== activeTab) {
      prevActiveTabRef.current = activeTab
      setPage(1)
    }
  }, [activeTab])

  // Store all loaded data
  const [membersData, setMembersData] = useState<PageData<RitualMember>>({
    data: [],
    total: 0,
    hasMore: false,
    page: 1,
    meta: { joinedCount: 0, savedCount: 0 },
  })
  const [savedData, setSavedData] = useState<PageData<RitualSavedUser>>({
    data: [],
    total: 0,
    hasMore: false,
    page: 1,
    meta: { joinedCount: 0, savedCount: 0 },
  })

  const navigate = useNavigate()
  const { mutate: createConversation, isPending: isCreatingConversation } =
    useCreateDmConversation()

  // Reset all state when modal closes
  useEffect(() => {
    if (!open) {
      setSearchQuery("")
      setDebouncedSearch("")
      setPage(1)
      setMembersData({
        data: [],
        total: 0,
        hasMore: false,
        page: 1,
        meta: { joinedCount: 0, savedCount: 0 },
      })
      setSavedData({
        data: [],
        total: 0,
        hasMore: false,
        page: 1,
        meta: { joinedCount: 0, savedCount: 0 },
      })
    }
  }, [open])

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery)
      setPage(1) // Reset to page 1 when search changes
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery])

  // Fetch data when page, search, or tab changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: activeTab is required to re-fetch data when user switches tabs
  useEffect(() => {
    const fetchData = async () => {
      if (!ritualId) return

      const currentPage = page
      const isFirstPage = currentPage === 1
      const isJoinedTab = activeTabRef.current === "joined"

      if (isFirstPage) {
        if (isJoinedTab) {
          setIsLoadingMembers(true)
        } else {
          setIsLoadingSaved(true)
        }
      } else {
        setIsLoadingMore(true)
      }

      try {
        if (isJoinedTab) {
          const result = await getRitualMembers(
            ritualId,
            currentPage,
            15,
            debouncedSearch || undefined,
          )
          setMembersData(prev => ({
            data: isFirstPage ? result.members : [...prev.data, ...result.members],
            total: result.total,
            hasMore: result.hasMore,
            page: currentPage,
            meta: result.meta,
          }))
          // Also update saved data meta
          setSavedData(prev => ({
            ...prev,
            meta: result.meta,
          }))
        } else {
          const result = await getRitualSavedUsers(
            ritualId,
            currentPage,
            15,
            debouncedSearch || undefined,
          )
          setSavedData(prev => ({
            data: isFirstPage ? result.users : [...prev.data, ...result.users],
            total: result.total,
            hasMore: result.hasMore,
            page: currentPage,
            meta: result.meta,
          }))
          // Also update members data meta
          setMembersData(prev => ({
            ...prev,
            meta: result.meta,
          }))
        }
      } catch (error) {
        console.error("Failed to fetch participants:", error)
      } finally {
        if (isJoinedTab) {
          setIsLoadingMembers(false)
        } else {
          setIsLoadingSaved(false)
        }
        setIsLoadingMore(false)
      }
    }

    fetchData()
  }, [ritualId, page, debouncedSearch, activeTab])

  const handleFollowToggle = (e: React.MouseEvent, userId: string, isFollowing: boolean) => {
    e.stopPropagation()
    // Optimistically update UI
    const updateData = <T extends RitualMember | RitualSavedUser>(prev: PageData<T>) => ({
      ...prev,
      data: prev.data.map(u => (u.id === userId ? { ...u, isFollowing: !isFollowing } : u)),
    })

    if (activeTabRef.current === "joined") {
      setMembersData(updateData)
    } else {
      setSavedData(updateData)
    }

    // TODO: Call actual API
  }

  const handleMemberClick = (userId: string) => {
    navigate(`/profile/${userId}`)
  }

  const handleLoadMore = () => {
    setPage(prev => prev + 1)
  }

  const currentData = activeTabRef.current === "joined" ? membersData : savedData
  const currentLoading = activeTabRef.current === "joined" ? isLoadingMembers : isLoadingSaved
  const isLoadingOrEmpty = currentLoading && currentData.data.length === 0

  const handleClose = () => {
    onOpenChange(false)
  }

  const handleClearSearch = () => {
    setSearchQuery("")
    setPage(1)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="lg:max-w-[1300px] h-[670px] p-0 overflow-hidden bg-[#0f0f0f] border-[#F2F0EB4D] flex flex-col gap-5">
        <div className="px-7.5 pt-8.5">
          <div className="flex items-center justify-between mb-6.25">
            <div className="flex items-center gap-2 sm:gap-3.75">
              <DialogTitle className="text-[18px] leading-7.5 font-semibold text-primary">
                Participants
              </DialogTitle>
              <div className="w-px h-[30px] bg-[#F2F0EB1A]" />
              <div className="flex items-center gap-3 sm:gap-5">
                <button
                  className={`text-sm cursor-pointer transition-colors ${
                    activeTab === "joined"
                      ? "text-white font-semibold"
                      : "text-[#FFFFFFB3] font-normal"
                  }`}
                  onClick={() => setActiveTab("joined")}
                >
                  Joined ({membersData.meta.joinedCount})
                </button>
                <button
                  className={`text-sm cursor-pointer transition-colors ${
                    activeTab === "saved"
                      ? "text-white font-semibold"
                      : "text-[#FFFFFFB3] font-normal"
                  }`}
                  onClick={() => setActiveTab("saved")}
                >
                  Saved ({savedData.meta.savedCount})
                </button>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          <div className="relative flex items-center h-[65px] px-[14px] rounded-[15px] border border-[#F2F0EB26] bg-[#F2F0EB0D]">
            <Search className="w-5 h-5 text-white/90 flex-shrink-0" />
            <input
              type="text"
              placeholder={t("participants.searchPlaceholder")}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent border-none outline-none text-[14px] font-normal text-white/90 placeholder:text-white/90 placeholder:font-normal px-3"
            />
            {searchQuery && (
              <button onClick={handleClearSearch} className="p-1 text-white/50 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Scrollable container */}
        <div className="overflow-y-auto px-6 flex-1">
          {/* Initial loading state */}
          {isLoadingOrEmpty ? (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-olive" />
              <span className="text-sm text-white/50">Loading participants...</span>
            </div>
          ) : currentData.data.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 pb-4">
              {currentData.data.map(user => (
                <div
                  key={user.id}
                  className="flex items-center justify-between gap-3 p-5 rounded-[20px] cursor-pointer bg-[#FFFFFF0D] border border-[#FFFFFF1A] hover:bg-[#FFFFFF1A] transition-colors"
                  onClick={() => handleMemberClick(user.id)}
                >
                  <div className="flex items-center gap-3.75">
                    <Avatar
                      src={user.profileCoverImage ?? undefined}
                      fallback={user.name || user.username || "?"}
                      size="lg"
                      className="lg:!w-[55px] lg:!h-[55px] !text-base"
                    />
                    <div className="flex flex-col gap-[1px] min-w-0">
                      <p
                        className={`${
                          user.id !== currentUserId ? "w-25 sm:w-35" : ""
                        } text-sm font-semibold text-primary truncate`}
                      >
                        {user.name || user.username}
                      </p>
                      {user.username && user.name && (
                        <p className="w-25 sm:w-35 text-xs leading-5 text-[#f2f0eb]/80 truncate">
                          @{user.username}
                        </p>
                      )}
                    </div>
                  </div>
                  {user.id !== currentUserId && (
                    <div className="flex items-center gap-[10px] flex-shrink-0">
                      <button
                        type="button"
                        className="p-1 text-[#f2f0eb] hover:text-[#f2f0eb]/70 transition-colors disabled:opacity-50"
                        onClick={e => {
                          e.stopPropagation()
                          createConversation(user.id, {
                            onSuccess: conversation => {
                              navigate(`/chats/dm/${conversation.id}`)
                            },
                          })
                        }}
                        disabled={isCreatingConversation}
                        title={t("participants.chat")}
                      >
                        <ChatBubbleIcon className="w-[20px] h-[20px]" />
                      </button>
                      <button
                        className={`p-1 transition-colors ${
                          user.isFollowing
                            ? "text-brand-olive hover:text-brand-olive/70"
                            : "text-[#f2f0eb] hover:text-[#f2f0eb]/70"
                        }`}
                        onClick={e => handleFollowToggle(e, user.id, user.isFollowing)}
                      >
                        {user.isFollowing ? (
                          <HeartFilledIcon className="w-[20px] h-[20px]" />
                        ) : (
                          <HeartOutlineIcon className="w-[20px] h-[20px]" />
                        )}
                      </button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="p-1 text-[#f2f0eb] hover:text-[#f2f0eb]/70 transition-colors"
                            onClick={e => e.stopPropagation()}
                            aria-label={t("participants.moreActions")}
                          >
                            <MoreVertical className="w-[20px] h-[20px]" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            className="text-brand-terracotta gap-2"
                            onClick={e => {
                              e.stopPropagation()
                              // Handle report
                            }}
                          >
                            <WarningIcon size={16} color="#C86757" />
                            Report
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="py-16 text-center text-sm text-white/50">
              {searchQuery
                ? t("participants.noUsersFound")
                : activeTab === "joined"
                  ? t("participants.noMembersYet")
                  : t("participants.noSavedUsersYet")}
            </div>
          )}

          {/* Loading more indicator */}
          {isLoadingMore && (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-olive" />
            </div>
          )}

          {/* Load More button */}
          {currentData.hasMore && !isLoadingMore && (
            <div className="flex justify-center py-4">
              <button
                onClick={handleLoadMore}
                className="px-6 py-2.5 text-sm font-medium text-white/80 border border-white/20 rounded-lg hover:bg-white/5 transition-colors"
              >
                Load More
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
